// React Query hooks for per-task reports (routes/reports/index.ts,
// docs/plans/2026-10-02-rapporten-contract.md §2). Keys live under
// ['reports', …]: ['reports', 'task', taskId], ['reports', 'project',
// projectId], ['reports', reportId, 'render', version] and
// ['reports', reportId, 'submissions'].

import { useCallback, useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, reportsApi } from '../lib/api-client'
import { useAuth } from '../lib/auth'
import type { ReportLastSubmission, ReportSubmitResult } from '../components/reports/report-types'

/**
 * What the signed-in user may do with reports (permission-resolver.service.ts):
 * `report.read` and `report.submit` = any workspace member (viewer too);
 * `report.write` = operator, administrator, owner — the same roles as
 * `kb.write`. The API stays authoritative; this only decides what to show.
 */
export function useReportPermissions(): { canSubmit: boolean; canWrite: boolean } {
  const roles = useAuth().user?.roles ?? []
  const has = (...names: string[]) => roles.some((role) => names.includes(role))
  return {
    canSubmit: roles.length > 0,
    canWrite: has('workspace_owner', 'administrator', 'operator'),
  }
}

export function taskReportKey(taskId: string): unknown[] {
  return ['reports', 'task', taskId]
}

/** `GET /tasks/:taskId/report`; `refetchInterval` while the task is live. */
export function useTaskReport(
  taskId: string | null | undefined,
  options: { refetchInterval?: number | false } = {}
) {
  return useQuery({
    queryKey: taskReportKey(taskId ?? ''),
    queryFn: () => reportsApi.forTask(taskId!),
    enabled: !!taskId,
    staleTime: 10_000,
    refetchInterval: options.refetchInterval ?? false,
  })
}

/**
 * The render document of one version. Never refetched by itself — a new
 * document reloads the iframe and would throw away what someone was filling
 * in. Signed asset URLs expire (`urlExpiresAt`); `useReportUrlsExpired`
 * tells the viewer, who reloads when it suits them.
 */
export function useReportRender(
  reportId: string | null | undefined,
  version: number | null | undefined
) {
  return useQuery({
    queryKey: ['reports', reportId ?? '', 'render', version ?? 0],
    queryFn: () => reportsApi.render(reportId!, version ?? undefined),
    enabled: !!reportId && !!version,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })
}

/** True once `urlExpiresAt` (ISO) has passed: the report's images stop loading. */
export function useReportUrlsExpired(urlExpiresAt: string | null | undefined): boolean {
  const [expired, setExpired] = useState(false)
  useEffect(() => {
    setExpired(false)
    if (!urlExpiresAt) return
    const remaining = Date.parse(urlExpiresAt) - Date.now()
    if (!Number.isFinite(remaining)) return
    if (remaining <= 0) {
      setExpired(true)
      return
    }
    // setTimeout caps at ~24.8 days; signed URLs live about an hour.
    const timer = window.setTimeout(() => setExpired(true), Math.min(remaining, 2 ** 31 - 1))
    return () => window.clearTimeout(timer)
  }, [urlExpiresAt])
  return expired
}

/**
 * The signed-in viewer's own latest submission to a report, fetched fresh
 * for each `goblic.getLastSubmission()` call (never someone else's).
 */
export function useLastOwnSubmission(
  reportId: string | null | undefined
): () => Promise<ReportLastSubmission | null> {
  return useCallback(async () => {
    if (!reportId) return null
    return (await reportsApi.latestOwnSubmission(reportId)).submission
  }, [reportId])
}

export function useReportSubmissions(reportId: string | null | undefined) {
  return useQuery({
    queryKey: ['reports', reportId ?? '', 'submissions'],
    queryFn: () => reportsApi.submissions(reportId!),
    enabled: !!reportId,
    staleTime: 10_000,
  })
}

export function useProjectReports(projectId: string | null | undefined) {
  return useQuery({
    queryKey: ['reports', 'project', projectId ?? ''],
    queryFn: () => reportsApi.forProject(projectId!),
    enabled: !!projectId,
    staleTime: 15_000,
  })
}

/**
 * `POST /reports/:id/submissions`, shaped for the panel: a refusal (4xx —
 * secret detected, rate limited, invalid) resolves with `{ ok: false,
 * error }` so the dialog and the report can show why; anything else throws.
 * The render is not refetched: that would reload the iframe the person just
 * sent from.
 */
export function useSubmitReport(reportId: string | null | undefined, taskId?: string) {
  const queryClient = useQueryClient()
  const mutation = useMutation({
    mutationFn: (input: { version: number; data: unknown }) =>
      reportsApi.submit(reportId!, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['reports', reportId ?? '', 'submissions'],
      })
      if (taskId) {
        void queryClient.invalidateQueries({ queryKey: taskReportKey(taskId) })
      }
    },
  })
  const { mutateAsync } = mutation
  return useCallback(
    async (data: unknown, version: number): Promise<ReportSubmitResult> => {
      if (!reportId) return { ok: false, error: 'This report can’t receive data.' }
      try {
        await mutateAsync({ version, data })
        return { ok: true }
      } catch (error) {
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
          return { ok: false, error: error.message }
        }
        throw error
      }
    },
    [reportId, mutateAsync]
  )
}

/**
 * Downloads `GET /reports/:id/export` in the parent page (never in the
 * iframe): fetch with the token, then a temporary object URL and a click
 * on a detached link.
 */
export function useDownloadReport(): {
  download: (reportId: string, version: number, fallbackName: string) => Promise<void>
  downloading: boolean
  error: unknown
} {
  const [downloading, setDownloading] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const download = useCallback(
    async (reportId: string, version: number, fallbackName: string) => {
      setDownloading(true)
      setError(null)
      try {
        const { blob, filename } = await reportsApi.export(reportId, version)
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = filename ?? `${fallbackName}-v${version}.html`
        link.rel = 'noopener'
        document.body.appendChild(link)
        link.click()
        link.remove()
        // Give the browser a moment to start the download before revoking.
        window.setTimeout(() => URL.revokeObjectURL(url), 10_000)
      } catch (err) {
        setError(err)
      } finally {
        setDownloading(false)
      }
    },
    []
  )
  return { download, downloading, error }
}
